import { useEffect, useState, useContext } from "react";
import { Card, Icon, Grid } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { LanguageContext } from "App";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import AccountingApi from "../../../../Classes/API/AccountingApi";
import SessionsApi from "../../../../Classes/API/Administration/SessionsApi";
import CreateMultiPackageReq from "../../../../Classes/Models/Requests/AccountingSessions/CreateMultiPackageReq";
import PatientSelector from "CinicComponents/PatientSelector";
import MultiPackageSession from "./MultiPackageSession";
import "./style.scss";
import ServicesApi from "../../../../Classes/API/Services/ServicesApi";

const CreatePackageIndex = () => {
  const navigate = useNavigate();
  const { translations } = useContext(LanguageContext);

  const [Patient, setPatient] = useState({});
  const [recentBundle, setRecentBundle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [PackageStartDate, setPackageStartDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });

  const [PackageSessions, setSessionIdsAndQuantities] = useState([]);
  const [patientProgramSessions, setpatientProgramSessions] = useState([]);

  const [TotalOriginalPrice, setTotalOriginalPrice] = useState(0);
  const [TotalQuantity, setTotalQuantity] = useState(0);
  const [TotalFinalPrice, setTotalFinalPrice] = useState(0);
  const [TotalDiscount, setTotalDiscount] = useState(0);

  const onAddSessionClick = () => {
    if (!Patient?.getPatient?.id) {
      Swal.fire({
        title: "Action Required",
        text: "Please select a patient first.",
        icon: "warning",
      });
      return;
    }
    setpatientProgramSessions((prev) => [...prev, { tempId: Math.random() }]);
  };

  const CopyPatientProgram = async () => {
    const patientId = Patient?.getPatient?.id;
    if (!patientId)
      return Swal.fire({ title: "Action Required", icon: "warning" });

    Swal.fire({
      title: "Fetching Program...",
      didOpen: () => Swal.showLoading(),
    });
    let res = await AccountingApi.GetPatientProgramDetails(patientId);
    Swal.close();

    if (res.status === "Success" && res.data.length > 0) {
      const confirmResult = await Swal.fire({
        title: "Confirm Copy",
        text: `Copy program from ${new Date(res.data[0].date).toLocaleDateString("en-GB")}?`,
        icon: "question",
        showCancelButton: true,
      });

      if (confirmResult.isConfirmed) {
        setSessionIdsAndQuantities([]);
        const dataWithIds = res.data.map((item) => ({
          ...item,
          tempId: Math.random(),
          isCopied: true,
        }));
        setpatientProgramSessions(dataWithIds);
      }
    } else {
      Swal.fire({ title: "No Program Found", icon: "info" });
    }
  };

  const CopyLastPackage = async () => {
    const patientId = Patient?.getPatient?.id;
    if (!patientId)
      return Swal.fire({ title: "Action Required", icon: "warning" });

    Swal.fire({
      title: "Fetching last package...",
      didOpen: () => Swal.showLoading(),
    });
    try {
      const res = await AccountingApi.GetLastPackageDetails(patientId);
      Swal.close();

      if (res.status === "Success" && res.data?.length > 0) {
        setSessionIdsAndQuantities([]);
        const dataWithIds = res.data.map((item) => ({
          ...item,
          tempId: Math.random(),
          isCopied: true,
        }));
        setpatientProgramSessions(dataWithIds);
        Swal.fire({
          icon: "success",
          title: "Copied!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ title: "No Package Found", icon: "info" });
      }
    } catch (err) {
      Swal.close();
    }
  };

  const onDeleteSession = (tempId) => {
    setpatientProgramSessions((prev) =>
      prev.filter((s) => s.tempId !== tempId),
    );
    setSessionIdsAndQuantities((prev) =>
      prev.filter((s) => s.tempId !== tempId),
    );
  };

  const setPkgSession = (pkgSession) => {
    setSessionIdsAndQuantities((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.tempId === pkgSession.tempId,
      );
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = pkgSession;
        return updated;
      }
      return [...prev, pkgSession];
    });
  };

  const onCreateClick = async () => {
    if (!Patient?.getPatient?.id) {
      Swal.fire({ title: "Error", text: "Invalid selection", icon: "error" });
      return;
    }

    setIsCreating(true);
    Swal.fire({
      title: "Creating Package...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const req = new CreateMultiPackageReq();
      req.packageName = `Package - ${Patient?.getPatient?.name || "New"}`;
      req.packageSessions = PackageSessions.filter((s) => s && s.sessionId).map(
        ({ tempId, ...rest }) => rest,
      );
      req.patientId = Patient.getPatient.id;
      req.startdate = PackageStartDate.toISOString().split("T")[0];

      const res = await SessionsApi.CreateMultiPackage(req);
      if (res.status === "Success") {
        Swal.fire({ title: "Success", icon: "success" }).then(() =>
          navigate("/Packages/Details/", { state: { pkg: res.data } }),
        );
      } else {
        Swal.fire({ title: "Error", text: res.message, icon: "error" });
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: "An error occurred", icon: "error" });
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    let tQty = 0,
      tOrig = 0,
      tFinal = 0;
    PackageSessions.forEach((s) => {
      if (s && s.quantity) {
        tQty += parseInt(s.quantity) || 0;
        tOrig +=
          (parseFloat(s.originalPrice) || 0) * (parseInt(s.quantity) || 0);
        tFinal += (parseFloat(s.finalPrice) || 0) * (parseInt(s.quantity) || 0);
      }
    });
    setTotalQuantity(tQty);
    setTotalOriginalPrice(tOrig);
    setTotalFinalPrice(tFinal);
    setTotalDiscount(tOrig - tFinal);
  }, [PackageSessions]);

  useEffect(() => {
    const fetchLastBundle = async () => {
      const patientId = Patient?.getPatient?.id || Patient?.id;
      if (patientId) {
        const res = await ServicesApi.LastServiceBundle(patientId, null);
        if (res.status === "Success" && res.data) {
          const createdAt = new Date(res.data.startDate || res.data.createdAt);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          if (createdAt >= threeMonthsAgo) {
            setRecentBundle(res.data);
          } else {
            setRecentBundle(null);
          }
        } else {
          setRecentBundle(null);
        }
      } else {
        setRecentBundle(null);
      }
    };
    fetchLastBundle();
  }, [Patient]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={3}>
        <Card sx={{ p: 3 }}>
          <MDBox display="flex" justifyContent="space-between" mb={3}>
            <MDTypography variant="h5" fontWeight="bold">
              Create Multi-Package
            </MDTypography>
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={CopyPatientProgram}
                disabled={isCreating}
              >
                <Icon>history</Icon>&nbsp; Copy Last Program
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                onClick={CopyLastPackage}
                disabled={isCreating}
              >
                <Icon>content_copy</Icon>&nbsp; Copy Last Package
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={onCreateClick}
                disabled={isCreating}
              >
                <Icon>{isCreating ? "sync" : "add"}</Icon>&nbsp;
                {isCreating ? "Creating..." : "Create Package"}
              </MDButton>
            </MDBox>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{ p: 3, border: "1px solid #e0e0e0", boxShadow: "none" }}
              >
                <MDInput
                  label="Start Date"
                  type="date"
                  value={PackageStartDate.toISOString().split("T")[0]}
                  onChange={(e) =>
                    setPackageStartDate(new Date(e.target.value))
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <MDBox mt={2}>
                  <PatientSelector
                    Patient={Patient}
                    setPatient={setPatient}
                    fullWidth
                  />
                </MDBox>
                {recentBundle && (
                  <MDBox
                    mt={1.5}
                    p={1.5}
                    sx={{
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #2196f3",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#d1e9ff" },
                    }}
                    onClick={() =>
                      navigate(
                        `/ViewServiceBundles/BundleDetails/${recentBundle.id}`,
                      )
                    }
                  >
                    <MDTypography
                      variant="caption"
                      color="info"
                      fontWeight="bold"
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, fontSize: "small !important" }}>
                        info
                      </Icon>
                      Patient had a service bundle in the last 3 months. Click
                      here to go to the service bundle.
                    </MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#f8f9fa",
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                }}
              >
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="button">Total Quantity</MDTypography>
                  <MDTypography variant="button" fontWeight="bold">
                    {TotalQuantity}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={1}>
                  <MDTypography variant="button">Original Price</MDTypography>
                  <MDTypography variant="button" fontWeight="bold">
                    EGP {TotalOriginalPrice.toFixed(2)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={1}>
                  <MDTypography variant="button">Discount</MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="error"
                  >
                    EGP {TotalDiscount.toFixed(2)}
                  </MDTypography>
                </MDBox>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  mt={1}
                  pt={1}
                  sx={{ borderTop: "1px solid #ccc" }}
                >
                  <MDTypography variant="h6">Final Price</MDTypography>
                  <MDTypography variant="h5" color="info">
                    EGP {TotalFinalPrice.toFixed(2)}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          <MDBox mt={4} display="flex" alignItems="center" gap={2}>
            <MDTypography variant="h6">
              {patientProgramSessions.length} Sessions
            </MDTypography>
            <MDButton
              color="dark"
              variant="outlined"
              size="small"
              onClick={onAddSessionClick}
              disabled={!Patient?.getPatient?.id || isCreating}
            >
              <Icon>add</Icon>&nbsp;Add Session
            </MDButton>
          </MDBox>

          <MDBox mt={2}>
            {(Patient?.getPatient?.id || Patient?.id) &&
              patientProgramSessions.map((sessionData) => (
                <Card
                  key={sessionData.tempId}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid #eee",
                    boxShadow: "none",
                  }}
                >
                  <MultiPackageSession
                    Patient={Patient}
                    setPkgSession={setPkgSession}
                    initialData={sessionData}
                    onDelete={() => onDeleteSession(sessionData.tempId)}
                  />
                </Card>
              ))}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default CreatePackageIndex;
